/**
 * @file documentService.ts
 * @brief Drive-backed document library API.
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';

export type LibraryDocumentKind = 'upload' | 'google_doc' | 'google_sheet' | 'drive_link';

export interface DocumentCategory {
  id: string;
  name: string;
  driveFolderId: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  documentCount: number;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  isRestricted?: boolean;
  myRole?: 'viewer' | 'editor' | null;
  pathOnly?: boolean;
}

export type FolderShareRole = 'viewer' | 'editor';

export interface FolderShare {
  id: string;
  categoryId: string;
  userId: string;
  email: string;
  userName: string;
  role: FolderShareRole;
  grantedBy: string;
  createdAt: string;
}

export interface LibraryDocument {
  id: string;
  categoryId: string;
  name: string;
  driveFileId: string;
  webViewUrl: string;
  mimeType?: string;
  size?: number;
  kind: LibraryDocumentKind;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  isFavorite?: boolean;
}

export interface DocumentTaskLink {
  taskId: string;
  taskRef?: string;
  title: string;
  status?: string;
  linkedAt?: string;
  linkedDocumentId?: string;
}

export interface DocumentActivityItem {
  id: string;
  action: string;
  documentId?: string | null;
  documentName?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  actorId: string;
  actorName?: string;
  detail?: string | null;
  createdAt: string;
}

function mapCategory(raw: Record<string, unknown>): DocumentCategory {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    driveFolderId: String(raw.driveFolderId ?? ''),
    parentId: raw.parentId != null && raw.parentId !== '' ? String(raw.parentId) : null,
    sortOrder: Number(raw.sortOrder ?? 0),
    isActive: Boolean(raw.isActive),
    documentCount: Number(raw.documentCount ?? 0),
    createdBy: String(raw.createdBy ?? ''),
    createdByName: raw.createdByName != null ? String(raw.createdByName) : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
    isRestricted: Boolean(raw.isRestricted),
    myRole:
      raw.myRole === 'editor' || raw.myRole === 'viewer'
        ? raw.myRole
        : null,
    pathOnly: Boolean(raw.pathOnly),
  };
}

function mapFolderShare(raw: Record<string, unknown>): FolderShare {
  return {
    id: String(raw.id ?? ''),
    categoryId: String(raw.categoryId ?? ''),
    userId: String(raw.userId ?? ''),
    email: String(raw.email ?? ''),
    userName: String(raw.userName ?? ''),
    role: raw.role === 'editor' ? 'editor' : 'viewer',
    grantedBy: String(raw.grantedBy ?? ''),
    createdAt: String(raw.createdAt ?? ''),
  };
}

function mapDocument(raw: Record<string, unknown>): LibraryDocument {
  return {
    id: String(raw.id ?? ''),
    categoryId: String(raw.categoryId ?? ''),
    name: String(raw.name ?? ''),
    driveFileId: String(raw.driveFileId ?? ''),
    webViewUrl: String(raw.webViewUrl ?? ''),
    mimeType: raw.mimeType != null ? String(raw.mimeType) : undefined,
    size: raw.size != null ? Number(raw.size) : undefined,
    kind: (raw.kind as LibraryDocumentKind) ?? 'upload',
    createdBy: String(raw.createdBy ?? ''),
    createdByName: raw.createdByName != null ? String(raw.createdByName) : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
    isFavorite: Boolean(raw.isFavorite),
  };
}

function mapTaskLink(raw: Record<string, unknown>): DocumentTaskLink {
  return {
    taskId: String(raw.taskId ?? ''),
    taskRef: raw.taskRef != null ? String(raw.taskRef) : undefined,
    title: String(raw.title ?? ''),
    status: raw.status != null ? String(raw.status) : undefined,
    linkedAt: raw.linkedAt != null ? String(raw.linkedAt) : undefined,
    linkedDocumentId: raw.linkedDocumentId != null ? String(raw.linkedDocumentId) : undefined,
  };
}

function mapActivity(raw: Record<string, unknown>): DocumentActivityItem {
  return {
    id: String(raw.id ?? ''),
    action: String(raw.action ?? ''),
    documentId: raw.documentId != null ? String(raw.documentId) : null,
    documentName: raw.documentName != null ? String(raw.documentName) : null,
    categoryId: raw.categoryId != null ? String(raw.categoryId) : null,
    categoryName: raw.categoryName != null ? String(raw.categoryName) : null,
    actorId: String(raw.actorId ?? ''),
    actorName: raw.actorName != null ? String(raw.actorName) : undefined,
    detail: raw.detail != null ? String(raw.detail) : null,
    createdAt: String(raw.createdAt ?? ''),
  };
}

export const documentService = {
  async getStatus(): Promise<{ configured: boolean; ready: boolean; message?: string }> {
    const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS.STATUS);
    const data = unwrapResponse<{ configured?: boolean; ready?: boolean; message?: string }>(response);
    return {
      configured: Boolean(data?.configured),
      ready: data?.ready !== false,
      message: data?.message,
    };
  },

  async listActivity(limit = 40): Promise<DocumentActivityItem[]> {
    const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS.ACTIVITY, { params: { limit } });
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapActivity) : [];
  },

  async listCategories(includeInactive = false): Promise<DocumentCategory[]> {
    const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS.CATEGORIES, {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapCategory) : [];
  },

  async createCategory(name: string, parentId?: string | null): Promise<DocumentCategory> {
    const response = await apiClient.post(API_ENDPOINTS.DOCUMENTS.CATEGORIES, {
      name,
      parentId: parentId || undefined,
    });
    return mapCategory(unwrapResponse<Record<string, unknown>>(response));
  },

  async updateCategory(
    categoryId: string,
    body: { name?: string; isActive?: boolean }
  ): Promise<DocumentCategory> {
    const response = await apiClient.patch(API_ENDPOINTS.DOCUMENTS.CATEGORY(categoryId), body);
    return mapCategory(unwrapResponse<Record<string, unknown>>(response));
  },

  async deactivateCategory(categoryId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.DOCUMENTS.CATEGORY(categoryId));
  },

  async deleteEmptyCategory(categoryId: string): Promise<{ deletedIds: string[]; name: string }> {
    const response = await apiClient.delete(API_ENDPOINTS.DOCUMENTS.CATEGORY(categoryId), {
      params: { permanent: true },
    });
    return unwrapResponse<{ deletedIds: string[]; name: string }>(response);
  },

  async listFolderShares(categoryId: string): Promise<FolderShare[]> {
    const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS.SHARES(categoryId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapFolderShare) : [];
  },

  async addFolderShare(
    categoryId: string,
    userId: string,
    role: FolderShareRole
  ): Promise<FolderShare> {
    const response = await apiClient.post(API_ENDPOINTS.DOCUMENTS.SHARES(categoryId), { userId, role });
    return mapFolderShare(unwrapResponse<Record<string, unknown>>(response));
  },

  async removeFolderShare(categoryId: string, shareId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.DOCUMENTS.SHARE(categoryId, shareId));
  },

  async listDocuments(categoryId: string): Promise<LibraryDocument[]> {
    const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS.LIST(categoryId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapDocument) : [];
  },

  async createGoogleFile(
    categoryId: string,
    kind: 'google_doc' | 'google_sheet',
    name: string
  ): Promise<LibraryDocument> {
    const response = await apiClient.post(API_ENDPOINTS.DOCUMENTS.CREATE(categoryId), { kind, name });
    return mapDocument(unwrapResponse<Record<string, unknown>>(response));
  },

  async addDriveLink(categoryId: string, name: string, url: string): Promise<LibraryDocument> {
    const response = await apiClient.post(API_ENDPOINTS.DOCUMENTS.CREATE(categoryId), {
      kind: 'drive_link',
      name,
      url,
    });
    return mapDocument(unwrapResponse<Record<string, unknown>>(response));
  },

  async uploadFile(categoryId: string, file: File, name?: string): Promise<LibraryDocument> {
    const formData = new FormData();
    formData.append('file', file);
    if (name?.trim()) formData.append('name', name.trim());
    const response = await apiClient.post(API_ENDPOINTS.DOCUMENTS.UPLOAD(categoryId), formData, {
      timeout: 120000,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return mapDocument(unwrapResponse<Record<string, unknown>>(response));
  },

  async moveDocument(documentId: string, categoryId: string): Promise<LibraryDocument> {
    const response = await apiClient.patch(API_ENDPOINTS.DOCUMENTS.DOCUMENT(documentId), { categoryId });
    return mapDocument(unwrapResponse<Record<string, unknown>>(response));
  },

  async setFavorite(documentId: string, favorite: boolean): Promise<void> {
    if (favorite) {
      await apiClient.post(API_ENDPOINTS.DOCUMENTS.FAVORITE(documentId));
    } else {
      await apiClient.delete(API_ENDPOINTS.DOCUMENTS.FAVORITE(documentId));
    }
  },

  async listDocumentTasks(documentId: string): Promise<DocumentTaskLink[]> {
    const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS.TASKS(documentId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapTaskLink) : [];
  },

  async deleteDocument(documentId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.DOCUMENTS.DELETE(documentId));
  },
};
